import React from 'react';
import { useTranslation } from 'react-i18next';
import dynamic from 'next/dynamic';
import { AdCards } from '@/components/AdCards/AdCards';
import * as S from '@/components/AdsSection/AdsSection.styles';
import { AdsSectionProps } from '@/components/AdsSection/AdsSection.types';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

const InfiniteLoader = dynamic(
  () =>
    import('@/components/InfiniteLoader/InfiniteLoader').then(
      mod => mod.InfiniteLoader
    ),
  { ssr: false }
);

const AdsSectionInner: React.FC<AdsSectionProps> = ({
  index,
  title,
  hasPaddingTop,
  bannerInList,
  inListDesktopBanner,
  bottomBanner,
  allAdsLinkData,
  onLoadMore,
  loadMoreRootMargin,
  ads,
  adsLoading,
  chunkSize,
  listDataStat,
  className,
  isBannerInListAtTheEnd,
  isBannerInListAfterThreeRows,
  dataTestId,
  noPadding = false,
  hasFirstCardPriority,
  adTransitionSource,
  layoutScope,
}) => {
  const { t } = useTranslation();
  const { href: allAdsLinkHref, className: allAdsLinkClassName } =
    allAdsLinkData ?? {};
  const Box = S.getBoxComponent(layoutScope);

  return (
    <>
      <Box
        $noPadding={noPadding}
        $hasPaddingTop={hasPaddingTop}
        className={className}
        data-testid={dataTestId || CommonTestIds.adsSection.section}
      >
        {title && (
          <S.TitleWrapper>
            <S.TitleBox>
              <S.Title
                as="h2"
                $variant="2"
                data-testid={CommonTestIds.adsSection.title}
              >
                {title}
              </S.Title>
              {allAdsLinkData &&
                (adsLoading ? (
                  <SkeletonLoader width={80} height={20} borderRadius={7} />
                ) : (
                  <S.AllAdsLink
                    href={allAdsLinkHref ?? ''}
                    className={allAdsLinkClassName}
                  >
                    {t('common.all')}
                  </S.AllAdsLink>
                ))}
            </S.TitleBox>
          </S.TitleWrapper>
        )}

        <AdCards
          ads={ads}
          isInitialLoading={adsLoading}
          chunkSize={chunkSize}
          listDataStat={listDataStat}
          bannerInList={bannerInList}
          inListDesktopBanner={inListDesktopBanner}
          bottomBanner={bottomBanner}
          isBannerInListAtTheEnd={isBannerInListAtTheEnd}
          isBannerInListAfterThreeRows={isBannerInListAfterThreeRows}
          hasFirstCardPriority={hasFirstCardPriority}
          sectionIndex={index}
          adTransitionSource={adTransitionSource}
        />
      </Box>

      {onLoadMore && (
        <InfiniteLoader onVisit={onLoadMore} rootMargin={loadMoreRootMargin} />
      )}
    </>
  );
};

export const AdsSection = React.memo(AdsSectionInner);
